'use strict';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React, {Component} from 'react';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import {Observable} from 'rxjs/Observable';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {shouldStateUpdate} from '../react-utils';
import {errorHandler} from '../rx-utils';
import {logger} from './logger';


I18nComponent.$inject = [
  '$translate',
];

/**
 * @typedef {Component} I18nComponent
 */

/**
 * @typedef {Object} ReactProp
 * @property {String} code Message code. Must be provided in the message properties files.
 * @property {String} prop The prop that the translated message code will be injected as in the wrapped component.
 * @property {Function} [params] Must return an object that maps the props to the mesage parameters expected by the message code.
 * @property {Function} [dynamic] Dynamically retrieve code and params from props
 */

/**
 * @param {Component|Function} WrappedComponent
 * @param {Array<ReactProp>} messages
 * @return {I18nComponent}
 * @constructor
 */
export function I18nComponent(WrappedComponent, messages) {
  let usesProps = false;
  if (!isArray(messages)) {
// eslint-disable-next-line max-len
    throw new Error(`I18nComponent received invalid configuration for ${WrappedComponent.name}. messages must be an Array of Objects.`);
  } else {
    messages.forEach(msg => {
      if (!isObject(msg)) {
// eslint-disable-next-line max-len
        throw new Error(`I18nComponent received invalid configuration for ${WrappedComponent.name}. messages must be an Array of Objects.`);
      }
      if (!isFunction(msg.dynamic) && (!isString(msg.code) || !isString(msg.prop))) {
// eslint-disable-next-line max-len
        throw new Error(`I18nComponent recieved invalid configuration for ${WrappedComponent.name}. Found invalid message. Expected 'code' and 'prop' to be Strings`);
      }

      if (isFunction(msg.params) || isFunction(msg.dynamic)) {
        usesProps = true;
      }
    });
  }

  return class I18nComponentInstance extends Component {
    constructor(props) {
      super(props);
      if (I18nComponent.dependencies.$translate.isReady()) {
        this.state = messages.reduce((state, msg) => {
          state[msg.prop] = msg.def || translateInit(msg, props);
          return state;
        }, {});
      } else {
        this.state = messages.reduce((state, msg) => {
          state[msg.prop] = msg.def || '';
          return state;
        }, {});
      }
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
        />
      );
    }

    componentWillReceiveProps(nextProps) {
      if (usesProps) {
        this.props$ && this.props$.next(nextProps);
      }
    }

    componentDidMount() {
      if (usesProps) {
        this.props$ = new ReplaySubject(1);
        this.props$.next(this.props);

        this.stateSubscription = Observable
          .combineLatest(
            ...messages
              .map(msg => {
                if (isFunction(msg.dynamic)) {
                  return this.props$
                    .throttleTime(300, void 0, {leading: true, trailing: true})
                    .switchMap(props => translateDynamic(msg.prop, msg.dynamic(props)))
                    .distinctUntilChanged(isEqual);
                } else if (isFunction(msg.params)) {
                  return this.props$
                    .throttleTime(300, void 0, {leading: true, trailing: true})
                    .switchMap(props => translate(msg.code, msg.prop, msg.params(props)))
                    .distinctUntilChanged(isEqual);
                } else {
                  return translate(msg.code, msg.prop);
                }
              }),
            mapMessageProps,
          )
          .catch(errorHandler(logger, 'has props in messages'))
          .subscribe(nextState => {
            if (shouldStateUpdate(this.state, nextState)) {
              this.setState(nextState);
            }
          });
      } else {
        this.stateSubscription = Observable
          .combineLatest(
            ...messages.map(msg => translate(msg.code, msg.prop)),
            mapMessageProps,
          )
          .catch(errorHandler(logger, 'simple static messages'))
          .subscribe(nextState => {
            if (shouldStateUpdate(this.state, nextState)) {
              this.setState(nextState);
            }
          });
      }
    }

    componentWillUnmount() {
      this.props$ = null;
      this.stateSubscription && this.stateSubscription.unsubscribe();
    }
  };
}

function translateDynamic(labelProp, msg) {
  if (!msg) {
    return Observable.of(getResult(labelProp, null));
  } else if (isString(msg)) {
    return Observable.of(getResult(labelProp, msg));
  } else {
    return translate(msg.code, labelProp, msg.params);
  }
}

function translate(message, labelProp, params) {
  if (!I18nComponent.dependencies.$translate) {
    return Observable.throw(new Error('I18nComponent has not been injected by the container.'));
  } else {
    return Observable.fromPromise(I18nComponent.dependencies.$translate(message, params))
      .map(value => getResult(labelProp, value));
  }
}

function getResult(labelProp, value) {
  return {
    key: labelProp,
    value,
  };
}

function translateInit(msg, props) {
  if (isFunction(msg.dynamic)) {
    return translateDynamicInstant(msg.dynamic(props));
  } else if (isFunction(msg.params)) {
    return translateInstant(msg.code, msg.params(props));
  } else {
    return translateInstant(msg.code);
  }
}

function translateInstant(message, params) {
  if (!I18nComponent.dependencies.$translate) {
    return '';
  } else {
    return I18nComponent.dependencies.$translate.instant(message, params);
  }
}

function translateDynamicInstant(msg) {
  if (!msg) {
    return null;
  } else if (isString(msg)) {
    return msg;
  } else {
    return translateInstant(msg.code, msg.params);
  }
}

function mapMessageProps(...messageProps) {
  const output = {};
  let i = messageProps.length;
  let data;

  while (i--) {
    data = messageProps[i];
    output[data.key] = data.value;
  }

  return output;
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/react-i18n/I18nComponent.js